import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import LoaderQrIcon from '~/assets/icons/svgs/loader-qr.svg';
import anonQRDefaultQr from '~/assets/images/anon-default-qr-code.webp';
import { Icon } from '~/components/ui/icon';
import { Text } from '~/components/ui/text';
import { ANONYMOUS_QR_TEMPLATES_LIST } from '~/constants/anonymousQR';
import { QRCODE_DYNAMIC_MICROSITE_TYPE } from '~/constants/qr-code';
import AnonymousDesignSection from '~/pages/HomePage/components/AnonymousDesignSection';
import AnonymousVCardPlusQR from '~/pages/HomePage/components/AnonymousVCardPlusQR';
import AnonymousWebsiteQR from '~/pages/HomePage/components/AnonymousWebsiteQR';
import { useQrCodeById } from '~/queries/qrCode';
import { getIsQrCodeStylesUpdating } from '~/store/reducers/anonQr/selectors';
import { COLORS } from '~/styles/colors';

import {
  StyledAvailableTemplateContainer,
  StyledBottomButton,
  StyledDivider,
  StyledDrawer,
  StyledMobileBlock,
  StyledMobileBlockTop,
  StyledQRTypeButton,
  StyledQRTypeItem,
  StyledQRTypeItemIconContainer,
  StyledTab,
  StyledTabs,
} from './styled';

type QRTemplate = {
  title: string;
  description: string;
  type: string;
  icon: string;
  disabled?: boolean;
};

type AnonymousQRMobileProps = {
  onClickQRType: (type: QRTemplate) => void;
  selectedQrTemplate: QRTemplate;
  setIsOpenedLastStep: (open: boolean) => void;
  websiteQrId?: string;
  isAvailableQr?: boolean;
  qrCodeId?: string;
  vCardId?: string;
  isPendingCreateQrCode?: boolean;
};

export default function AnonymousQRMobile({
  onClickQRType,
  selectedQrTemplate,
  setIsOpenedLastStep,
  websiteQrId,
  isAvailableQr,
  qrCodeId,
  vCardId,
  isPendingCreateQrCode,
}: AnonymousQRMobileProps) {
  const { t } = useTranslation();

  const isQrCodeStylesUpdating = useSelector(getIsQrCodeStylesUpdating);

  const [tabsValue, setTabsValue] = useState<'content' | 'design'>('content');
  const [isOpenedDrawer, setIsOpenedDrawer] = useState(false);
  const [isLoaderIconLoaded, setIsLoaderIconLoaded] = useState(false);
  const [isQrImageLoaded, setIsQrImageLoaded] = useState(false);

  const { data: qrCodeData } = useQrCodeById({ qrCodeId, select: (res) => res.data });

  const qrImage = isAvailableQr ? qrCodeData?.qrImage : anonQRDefaultQr;

  useEffect(() => {
    if (qrImage && isQrImageLoaded) {
      setIsQrImageLoaded(false);
    }
  }, [qrImage]);

  const handleChangeTab = (newValue: 'content' | 'design') => {
    if (tabsValue !== newValue) {
      setTabsValue(newValue);
    }
  };

  const handleSelectQRType = (type: QRTemplate) => {
    onClickQRType(type);
    setIsOpenedDrawer(false);
  };

  const renderQRTypeContent = () => {
    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE) {
      return (
        <StyledAvailableTemplateContainer>
          <AnonymousWebsiteQR websiteQrId={websiteQrId} qrCodeId={qrCodeId} />
          <StyledBottomButton onClick={() => setIsOpenedLastStep(true)} disabled={isPendingCreateQrCode}>
            {t('buttons:downloadQr')}
          </StyledBottomButton>
        </StyledAvailableTemplateContainer>
      );
    }

    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS) {
      return (
        <StyledAvailableTemplateContainer>
          <AnonymousVCardPlusQR qrCodeData={qrCodeData} vCardId={vCardId} />
          <StyledBottomButton onClick={() => setIsOpenedLastStep(true)} disabled={isPendingCreateQrCode}>
            {t('buttons:downloadQr')}
          </StyledBottomButton>
        </StyledAvailableTemplateContainer>
      );
    }

    return (
      <StyledBottomButton onClick={() => setIsOpenedLastStep(true)}>{t('buttons:loginCreate')}</StyledBottomButton>
    );
  };

  const renderQRTypeDesign = () => {
    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE) {
      return (
        <>
          <AnonymousDesignSection qrCodeId={qrCodeId} />
          <StyledBottomButton onClick={() => setIsOpenedLastStep(true)}>{t('buttons:downloadQr')}</StyledBottomButton>
        </>
      );
    }

    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS) {
      return (
        <>
          <AnonymousDesignSection qrCodeId={qrCodeId} />
          <StyledBottomButton onClick={() => setIsOpenedLastStep(true)}>{t('buttons:downloadQr')}</StyledBottomButton>
        </>
      );
    }

    return (
      <StyledBottomButton onClick={() => setIsOpenedLastStep(true)}>{t('buttons:loginCreate')}</StyledBottomButton>
    );
  };

  return (
    <>
      <StyledMobileBlockTop>
        <Text variant="inputSmall" sx={{ marginBottom: '8px' }}>
          {t('createQrStep1:preview')}
        </Text>
        <img src={LoaderQrIcon} alt="" onLoad={() => setIsLoaderIconLoaded(true)} style={{ display: 'none' }} />
        <img src={qrImage} alt="" onLoad={() => setIsQrImageLoaded(true)} style={{ display: 'none' }} />
        {(isQrCodeStylesUpdating || isPendingCreateQrCode || !isQrImageLoaded || !qrImage) && isLoaderIconLoaded ? (
          <img src={LoaderQrIcon} alt="" />
        ) : (
          <img src={qrImage} alt="QR code" />
        )}
      </StyledMobileBlockTop>
      <StyledMobileBlock>
        <StyledTabs>
          <StyledTab $isSelected={tabsValue === 'content'} onClick={() => handleChangeTab('content')}>
            1. {t('basicTemplateLabels:contentTitle')}
          </StyledTab>
          <StyledTab $isSelected={tabsValue === 'design'} onClick={() => handleChangeTab('design')}>
            2. {t('basicTemplateLabels:tmplDesign')}
          </StyledTab>
        </StyledTabs>
        <StyledDivider />
        {tabsValue === 'content' && (
          <>
            <StyledQRTypeButton onClick={() => setIsOpenedDrawer(true)}>
              <Icon icon={selectedQrTemplate.icon} withParentColor={false} size={24} />
              <Text variant="input15" sx={{ color: COLORS.primary }}>
                {t(selectedQrTemplate.title)}
              </Text>
              <ChevronLeftIcon sx={{ rotate: '-90deg', marginLeft: 'auto' }} />
            </StyledQRTypeButton>
            {renderQRTypeContent()}
          </>
        )}
        {tabsValue === 'design' && renderQRTypeDesign()}
      </StyledMobileBlock>
      <StyledDrawer anchor="bottom" open={isOpenedDrawer} onClose={() => setIsOpenedDrawer(false)}>
        <div>
          <Text variant="h3" sx={{ marginBottom: '16px' }}>
            {t('landing:anonymQr.selectQrType')}
          </Text>
          {ANONYMOUS_QR_TEMPLATES_LIST.map((type) => {
            const isSelected = type.title === selectedQrTemplate.title;
            const isMore = type.type === 'more';

            return (
              <StyledQRTypeItem
                key={type.title}
                $isSelected={isSelected}
                onClick={() => handleSelectQRType(type)}
                disabled={type.disabled}
                aria-label={t(type.title)}
              >
                <StyledQRTypeItemIconContainer $isSelected={isSelected} $isMore={isMore}>
                  <Icon icon={type.icon} withParentColor={false} size={22} />
                </StyledQRTypeItemIconContainer>
                <Text
                  variant="buttonSmallMedium"
                  sx={{ color: isSelected ? COLORS.primary : COLORS.grey400, textAlign: 'center' }}
                >
                  {t(type.title)}
                </Text>
              </StyledQRTypeItem>
            );
          })}
        </div>
      </StyledDrawer>
    </>
  );
}
